<script setup>
const localePath = useLocalePath();
const { locale, t } = useI18n();

const props = defineProps({
  space: {
    type: Array,
    required: true,
  },
  price: {
    type: String,
    required: true,
  },
  copy: {
    type: String,
    required: false
  },
  eventEquipment: {
    type: Array
  },
  eventOtherEquipment: {
    type: Array
  },
  benefits: {
    type: Array
  },
  eventTypes: {
    type: Array
  }
});

const getIframeKey = (value) => {
	let key = value
	if (key === 'maximilian') return 'opero_maxmilian'
	if (key === 'akce') return 'opero_event'
	if (key === 'manes') return 'opero'

	return `opero_${key}`
}

const eventEquipment = computed(() => [
  {
    icon: "ikonky-01",
    label: locale.value === "cs" ? "Ozvučení (mikrofony, mixážní pult a další technika)" : "Sound system (microphones, mixing console, and other equipment)"
  },
  {
    icon: "ikonky-04",
    label: locale.value === "cs" ? "Projektor" : "Projector"
  },
  {
    icon: "ikonky-08",
    label: locale.value === "cs" ? "Základní divadelní osvětlení" : "Basic theater lighting"
  },
  {
    icon: "ikonky-09",
    label: locale.value === "cs" ? "Mobiliář (konferenční židle, stolky, řečnický pultík, designová křesílka, stoly pro catering, štendry apod.)" : "Furniture (conference chairs, small tables, speaker podium, designer armchairs, catering tables, clothing racks, etc.)"
  },
  {
    icon: "ikonky-11",
    label: locale.value === "cs" ? "Zasedací místnost Salvátor, která slouží jako backstage nebo zázemí pro catering" : "Meeting room \"Salvátor,\" which serves as a backstage or catering area"
  },
])

const eventOtherEquipment = computed(() => [
  {
    icon: "ikonky-03",
    label: locale.value === "cs" ? "Catering" : "Catering"
  },
  {
    icon: "ikonky-06",
    label: locale.value === "cs" ? "Profi technické zajištění (režie, záznam, stream, tlumočnická technika apod.)" : "Professional technical support (direction, recording, streaming, interpretation equipment, etc.)"
  },
  {
    icon: "ikonky-07",
    label: locale.value === "cs" ? "Hostesky, security, fotograf, moderátor atd." : "Hostesses, security, photographer, moderator, etc."
  },
  {
    icon: "ikonky-12",
    label: locale.value === "cs" ? "Doprovodný program na míru (DJ, živá hudba, taneční vystoupení atd.)" : "Custom entertainment program (DJ, live music, dance performances, etc.)"
  },
  {
    icon: "ikonky-14",
    label: locale.value === "cs" ? "Obsahové doplnění vašeho programu na zadané téma (ESG, vzdělávání, ekonomika, obchod a marketing, longevity apod.)" : "Content addition to your program on specified topics (ESG, education, economics, business and marketing, longevity, etc.)"
  },
  {
    icon: "ikonky-13",
    label: locale.value === "cs" ? "Dekorace, branding, fotostěny apod." : "Decorations, branding, photo walls, etc."
  },
]);

const benefits = computed(() => [
  {
    icon: "icon-3.svg",
    label: locale.value === "cs" ? "Atmosféra unikátního historického domu a genius loci" : "The atmosphere of a unique historical building and its genius loci"
  },
  {
    icon: "ikonky-11.png",
    label: locale.value === "cs" ? "Propojení vnitřních a venkovních prostor" : "Connection between indoor and outdoor spaces"
  },
  {
    icon: "icon-12.svg",
    label: locale.value === "cs" ? "Lokalita v historickém centru Prahy 1 s dobrou dostupností" : "Location in the historic center of Prague 1 with excellent accessibility"
  },
  {
    icon: "ikonky-14.png",
    label: locale.value === "cs" ? "Zavedený prostor s komunitou, která řeší témata podnikání a vzdělávání" : "Established space with a community focused on business and education topics"
  },
  {
    icon: "icon-11.svg",
    label: locale.value === "cs" ? "Individuální přístup eventového týmu a služby na míru" : "Individual approach of the event team and tailored services"
  },
  {
    icon: "ikonky-15.png",
    label: locale.value === "cs" ? "Vybraná zajímavá témata můžeme podpořit promem či slevou" : "Selected interesting topics can be supported with promotion or discounts"
  },
]);

const eventTypes = computed(() => [
  {
    image: "/img/event-space-photos/1.jpg",
    heading: locale.value === "cs" ? "Konference a panelové diskuze" : "Conferences and Panel Discussions",
    copy: locale.value === "cs" ? "Zajistíme kompletní produkci od techniky, registrace hostů až po koordinaci řečníků a časového harmonogramu. Po celou dobu je přítomný zkušený koordinátor, který hlídá hladký průběh akce." : "We provide complete production, from technical support and guest registration to coordinating speakers and the event schedule. An experienced coordinator will be present throughout the event to ensure smooth proceedings."
  },
  {
    image: "/img/event-space-photos/2.jpg",
    heading: locale.value === "cs" ? "Číše vína a recepce" : "Soireés and Receptions",
    copy: locale.value === "cs" ? "Postaráme se o uspořádání prostoru, catering i servis tak, aby večer působil profesionálně a reprezentativně. Hlídáme plynulost programu i komfort vašich hostů." : "We will take care of arranging the space, catering, and service to ensure the evening feels professional and representative. We monitor the smooth flow of the program and the comfort of your guests."
  },
  {
    image: "/img/event-space-photos/3.jpg",
    heading: locale.value === "cs" ? "Workshopy a školení" : "Workshops and Trainings",
    copy: locale.value === "cs" ? "Postaráme se o uspořádání prostoru, catering i servis tak, aby večer působil profesionálně a reprezentativně. Hlídáme plynulost programu i komfort vašich hostů." : "We will prepare the space, technology, and organizational arrangements so the instructor can fully focus on the content. We coordinate registration, refreshments, and the event's time frame."
  },
  {
    image: "/img/event-space-photos/4.jpg",
    heading: locale.value === "cs" ? "Networkingová setkání" : "Networking Meetings",
    copy: locale.value === "cs" ? "Zajistíme vhodné uspořádání prostoru, technické zázemí i servis pro přirozené propojování hostů. Dbáme na hladký průběh a profesionální atmosféru." : "We will ensure the appropriate space setup, technical support, and service to facilitate natural networking among guests. We focus on smooth execution and a professional atmosphere."
  },
  {
    image: "/img/event-space-photos/5.jpg",
    heading: locale.value === "cs" ? "Tiskové konference" : "Press Conferences",
    copy: locale.value === "cs" ? "Zajistíme vhodné uspořádání prostoru, technické zázemí i servis pro přirozené propojování hostů. Dbáme na hladký průběh a profesionální atmosféru." : "We provide technical support for the media, including sound, projection, and space for interviews. We coordinate the schedule and the arrival of journalists to ensure everything runs smoothly and without complications."
  },
  {
    image: "/img/event-space-photos/6.jpg",
    heading: locale.value === "cs" ? "Křty knih a launch eventy" : "Book Launches and Events",
    copy: locale.value === "cs" ? "Zajistíme vhodné uspořádání prostoru, technické zázemí i servis pro přirozené propojování hostů. Dbáme na hladký průběh a profesionální atmosféru." : "We take care of the technology, script, and organization of the key moment of the evening. We ensure the event runs professionally and has a pleasant atmosphere."
  },
  {
    image: "/img/event-space-photos/7.jpg",
    heading: locale.value === "cs" ? "Focení a natáčení" : "Photography and Filming",
    copy: locale.value === "cs" ? "Zajistíme vhodné uspořádání prostoru, technické zázemí i servis pro přirozené propojování hostů. Dbáme na hladký průběh a profesionální atmosféru." : "We offer representative spaces with flexible arrangements and technical support. We coordinate production and provide assistance throughout the entire process."
  },
  {
    image: "/img/event-space-photos/8.jpg",
    heading: locale.value === "cs" ? "Oslavy a svatby" : "Celebrations and Weddings",
    copy: locale.value === "cs" ? "Zajistíme vhodné uspořádání prostoru, technické zázemí i servis pro přirozené propojování hostů. Dbáme na hladký průběh a profesionální atmosféru." : "We will prepare the space, coordinate vendors, and handle technology and service. You can enjoy your day without any organizational worries."
  },
]);

</script>

<template>
  <div class="flex justify-between pt-10 lg:pt-20">
    <div class="max-w-7xl md:grid grid-cols-12 md:gap-16 gap-32 px-6">
      <div class="col-span-3">
        <div class="mb-16" v-if="space.key === 'akce'">
          <h3 class="side-heading mb-6">{{ locale === "cs" ? "Kapacita" : "Capacity" }}</h3>
          <div class="flex items-center gap-6 mt-2">
            <img width="40" src="/img/event-space-icons/ikonky-05.png" />
            <p><strong>{{ locale === "cs" ? "120 hostů" : "120 guests" }}</strong> {{ locale === "cs" ? " při divadelním uspořádání" : "in theater setup" }}</p>
          </div>
          <div class="flex items-center gap-6 mt-4">
            <img width="40" src="/img/event-space-icons/ikonky-10.png" />
            <p><strong>{{ locale === "cs" ? "200 hostů" : "200 guests" }}</strong> {{ locale === "cs" ? "pro společenské akce bez sezení" : "for social events without seating" }}</p>
          </div>

          <div class="mt-6 mb-12">
            <button
              class="pointer-events-auto whitespace-nowrap bg-transparent border rounded-full border-black px-5 py-2.5 text-sm"
              @click="
                space.key == 'akce'
                  ? navigateTo({
                      path: localePath({ name: 'contact' }),
                      query: { from: 'booking', type: 'space' },
                      hash: '#se-webform-8'
                    })
                  : navigateTo({
                      path: localePath({ name: 'contact' }),
                      query: { from: 'event', type: 'meeting_room' },
                      hash: '#se-webform-8'
                    })
              "
            >
              {{ t("spaces.meetingrooms." + space.key + ".button") }}
            </button>
            <a
              :href="`https://www.virtusfera.cz/${getIframeKey(space.key)}`"
              target="_blank"
              rel="noopener noreferrer"
              v-if="space.key !== 'amelie'"
            >
              <button
                class="mt-4 pointer-events-auto whitespace-nowrap bg-transparent border rounded-full border-black px-5 py-2.5 text-sm flex items-center space-x-2"
              >
                <img src="/img/icon-virtual-tour.svg" alt="Virtual tour icon" />
                <span>{{ t("homepage.virtual-tour") }}</span>
              </button></a
            >
          </div>

          <h3 class="side-heading mt-16">{{ locale === "cs" ? "Vybavení v ceně pronájmu" : "Equipment Included in the Rental Price" }}</h3>
          <div v-for="(equipment, i) in eventEquipment" :key="i">
            <div class="flex items-start gap-4 mt-4">
              <img width="40" :src="'/img/event-space-icons/' + equipment.icon + '.png'" />
              <p>{{ equipment.label }}</p>
            </div>
          </div>

          <h3 class="side-heading mt-16">{{ locale === "cs" ? "Naše další služby" : "Our Additional Services" }}</h3>

          <div v-for="(equipment, i) in eventOtherEquipment" :key="i">
            <div class="flex items-start gap-4 mt-4">
              <img width="40" :src="'/img/event-space-icons/' + equipment.icon + '.png'" />
              <p>{{ equipment.label }}</p>
            </div>
          </div>

          <h3 class="side-heading mt-14 mb-6">{{ locale === "cs" ? "Základní cena pronájmu" : "Basic Rental Price" }}</h3>
          <p><strong>{{ locale === "cs" ? "14 000 Kč/hodina" : "14,000 CZK/hour" }}</strong></p>
          <p>{{ locale === "cs" ? "8:00 – 18:00" : "8:00 AM – 6:00 PM" }}</p>
          <p class="mt-6"><strong>{{ locale === "cs" ? "10 000 Kč/hodina" : "10,000 CZK/hour" }}</strong></p>
          <p>{{ locale === "cs" ? "18:00 – 00:00" : "6:00 PM – 12:00 AM" }}</p>
          <p class="mt-6">{{ locale === "cs" ? "Pronájmy o víkendu jsou až o 50 % levnější." : "Weekend rentals are up to 50% cheaper." }}</p>
          <p class="mt-6"><strong>{{ locale === "cs" ? "Na půldenní a celodenní akce poskytujeme výrazné slevy." : "We offer significant discounts for half-day and full-day events." }}</strong></p>
          <p class="mt-6">{{ locale === "cs" ? "Ceny jsou uvedeny bez DPH." : "Prices are excluding VAT." }}</p>

          <h3 class="side-heading mt-16 mb-4">{{ locale === "cs" ? "Produkční náklady" : "Production Costs" }}</h3>
          <p>{{ locale === "cs" ? "Produkční náklady 7 % celkové částky fakturace (event manažerka, úprava prostor a úklid)" : "Production costs 7% of the total invoiced amount (event manager, space setup, and cleaning)" }}</p>
        </div>

        <div v-if="space.price && space.key !== 'akce'" class="pt-6">
					<div v-if="space.key === 'disabled'">
						<p class="text-xl mb-8 bg-primary p-4">
							<strong>{{ locale === 'en' ? 'Coming soon. The meeting room will be available for reservations starting in September 2025.' : 'Připravujeme. Zasedací místnost bude k dispozici pro rezervace od září 2025.' }}</strong>
						</p>
					</div>

          <p class="text-xl mb-1" v-html="space.price[locale].size" />
          <p class="text-xl font-medium mb-1">
            <span v-if="locale === 'en'" class="font-medium">CZK </span
            >
            <span class="font-medium">{{
              new Intl.NumberFormat(locale === "cs" ? "cs-CZ" : "en-GB").format(
                price
              )
            }}</span>
            <span v-if="locale === 'cs'" class="font-medium"> Kč </span>
            <span class="font-medium"> / {{ t("spaces.meetingrooms.hour") }}</span>
          </p>
          <p v-if="space.price[locale].day" class="text-xl font-medium mb-1">
            {{ space.price[locale].day }}
          </p>

          <p class="mb-6 mt-1.5 text-[13px] font-light">
            {{ t("princeIncludeVAT") }}
          </p>
        </div>

        <div v-if="false" class="pt-6">
          <!-- Used to be for "akce" -->
          <p class="text-lg mb-3">
            <strong>{{ t("general.price") }}</strong>
          </p>
          <p class="mb-2">
            <strong>{{ t("general.working-days") }}</strong>
          </p>

          <p class="text-[13px] leading-5">
            {{
              locale === "cs"
                ? "08:00 - 18:00 14 000 Kč/hodina"
                : "8:00 am – 6:00 pm CZK 14,000 / hour"
            }}
          </p>
          <p class="text-[13px] leading-5 mb-2">
            {{
              locale === "cs"
                ? "18:00 - 08:00 10 000 Kč/hodina"
                : "6:00 pm – 8:00 am CZK 10,000 / hour"
            }}
          </p>

          <p class="mb-2">
            <strong>{{ t("general.weekends") }}</strong>
          </p>

          <p class="text-[13px] leading-5">
            {{
              locale === "cs"
                ? "08:00 - 18:00 7 000 Kč/hodina"
                : "8:00 am – 6:00 pm CZK 7,000 / hour"
            }}
          </p>
          <p class="text-[13px] leading-5 mb-2">
            {{
              locale === "cs"
                ? "18:00 - 08:00 9 000 Kč/hodina"
                : "6:00 pm – 8:00 am CZK 9,000 / hour"
            }}
          </p>
          <p class="text-[13px] leading-5 mb-2">
            {{
              locale === "cs"
                ? "Venkovní atrium 5 000 Kč/hodina"
                : "Outdoor atrium CZK 5,000 / hour"
            }}
          </p>

          <!-- <p class="text-lg mb-3">
            <strong>{{ t("general.price-packages") }}</strong>
          </p> -->

          <!-- <p class="mb-2">
            <strong>{{ t("general.working-days") }}</strong>
          </p>
          <p class="text-[13px] leading-5 mb-2">
            {{ t("general.working-days-prices") }}
          </p>

          <p class="mb-2">
            <strong>{{ t("general.weekends") }}</strong>
          </p>
          <p class="text-[13px] leading-5 mb-2">
            {{ t("general.weekdays-days-prices") }}
          </p> -->

          <p class="text-[13px] leading-5 mb-2">
            {{ t("event.price.running-costs") }}
          </p>

          <p class="text-[13px] leading-5">
            {{ t("general.price-includes-vat") }}
          </p>
        </div>
        <div class="mt-6 mb-12">
          <button
            class="pointer-events-auto whitespace-nowrap bg-transparent border rounded-full border-black px-5 py-2.5 text-sm"
            @click="
              space.key == 'akce'
                ? navigateTo({
                    path: localePath({ name: 'contact' }),
                    query: { from: 'booking', type: 'space' },
                    hash: '#se-webform-8'
                  })
                : navigateTo({
                    path: localePath({ name: 'contact' }),
                    query: { from: 'event', type: 'meeting_room' },
                    hash: '#se-webform-8'
                  })
            "
          >
            {{ t("spaces.meetingrooms." + space.key + ".button") }}
          </button>
          <a
            :href="`https://www.virtusfera.cz/${getIframeKey(space.key)}`"
            target="_blank"
            rel="noopener noreferrer"
						v-if="space.key !== 'amelie'"
          >
            <button
              class="mt-4 pointer-events-auto whitespace-nowrap bg-transparent border rounded-full border-black px-5 py-2.5 text-sm flex items-center space-x-2"
            >
              <img src="/img/icon-virtual-tour.svg" alt="Virtual tour icon" />
              <span>{{ t("homepage.virtual-tour") }}</span>
            </button></a
          >
        </div>

        <div class="block sm:flex md:block justify-start gap-20">
          <div>
            <div v-if="space.equipment && space.equipment.length">
              <div class="txt-t mb-2">
                {{ t(space.key == 'otakar' ? "spaces.meetingromms.equipment2" : "spaces.meetingromms.equipment") }}
              </div>
              <div v-for="(equipment, i) in space.equipment" :key="i">
                <div class="flex items-center gap-2 mt-2">
                  <img :src="'/img/icon-' + equipment + '.svg'" />
                  <p>{{ t("spaces.meetingrooms.equipment." + equipment) }}</p>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div v-if="space.services && space.services.length" class="txt-t mb-2 mt-8 sm:mt-0 md:mt-8">
              {{ t("spaces.meetingromms.weprovide") }}
            </div>
            <div v-if="space.services">
              <div v-for="(service, i) in space.services" :key="i">
                <div class="flex items-center gap-2 mt-2">
                  <img :src="'/img/icon-' + service + '.svg'" />
                  <p>{{ t("spaces.meetingrooms.equipment." + service) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-start-5 col-span-8 mt-12 md:mt-0">
        <div v-if="space.subtitle" class="pb-8 txt-q">{{ t(space.subtitle) }}</div>
        <div class="pb-12">
          <div class="space-copy" v-if="copy" v-html="copy"></div>
          <p v-if="!copy" v-for="(text, i) in space.text" :key="i" class="mb-6">
            {{ t(text) }}
          </p>
          <p v-if="space.key !== 'kavarna'">{{ t("spaces.meetingrooms.copy_end") }}</p>
          <div v-if="space.key === 'akce'">
            <h2 class="txt-q mt-10 mb-10">{{ locale === "cs" ? "V čem jsme výjimeční?" : "What makes us unique?" }}</h2>
            <div class="benefits mb-20">
              <div v-for="(benefit, i) in benefits" :key="i">
                <div class="flex items-center gap-2">
                  <img width="40" :src="'/img/event-space-icons/' + benefit.icon" />
                  <p>{{ benefit.label }}</p>
                </div>
              </div>
            </div>
            <h2 v-if="locale === 'cs'" class="txt-q mt-20 mb-10">{{ locale === "cs" ? "Co v Operu nejčastěji pořádáme?" : "" }}</h2>
            <div class="event-types">
              <div class="event-type" v-for="(item, i) in eventTypes" :key="i">
                <div class="event-type-image">
                  <img :src="item.image" :alt="item.heading" />
                </div>
                <h3>{{ item.heading }}</h3>
                <p class="event-type-copy">{{ item.copy }}</p>
              </div>
            </div>
            <div class="mt-6 mb-12 cta">
              <button
                class="pointer-events-auto whitespace-nowrap bg-transparent border rounded-full border-black px-5 py-2.5 text-sm"
                @click="
                  space.key == 'akce'
                    ? navigateTo({
                        path: localePath({ name: 'contact' }),
                        query: { from: 'booking', type: 'space' },
                        hash: '#se-webform-8'
                      })
                    : navigateTo({
                        path: localePath({ name: 'contact' }),
                        query: { from: 'event', type: 'space' },
                        hash: '#se-webform-8'
                      })
                "
              >
                {{ t("spaces.meetingrooms." + space.key + ".button") }}
              </button>
              <a
                :href="`https://www.virtusfera.cz/${getIframeKey(space.key)}`"
                target="_blank"
                rel="noopener noreferrer"
                v-if="space.key !== 'amelie'"
              >
                <button
                  class="mt-4 pointer-events-auto whitespace-nowrap bg-transparent border rounded-full border-black px-5 py-2.5 text-sm flex items-center space-x-2"
                >
                  <img src="/img/icon-virtual-tour.svg" alt="Virtual tour icon" />
                  <span>{{ t("homepage.virtual-tour") }}</span>
                </button></a
              >
            </div>
          </div>
        </div>
        <div class="pb-20">
          <slot></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.side-heading {
  font-size: 22px;
  font-weight: 300;
}

button {
  &:hover {
    transition-duration: 350ms;
    img {
      filter: brightness(0) invert(1);
    }
  }
}

.benefits {
  display: grid;
  grid-template-columns: 1fr;
  padding-left: 40px;
  gap: 15px 20px;

  @media all and (min-width: 680px) {
    grid-template-columns: 1fr 1fr;
  }
}

.event-types {
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px 20px;

  @media all and (min-width: 560px) {
    grid-template-columns: 1fr 1fr;
  }
}

.event-type {
  &-image {
    margin-bottom: 20px;
  }

  &-copy {
    margin-top: 30px;
  }
}

.cta {
  display: flex;
  text-align: center;
  margin-top: 80px;
  align-items: center;
  justify-content: center;
  gap: 10px;

  @media all and (max-width: 560px) {
    flex-direction: column;
  }

  a {
    display: inline-block;

    button {
      margin: 0;
    }
  }
}
</style>
